import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    autoClose: {
      type: Boolean,
      attribute: 'auto-close',
    },
  }

  constructor() {
    super()

    this.closeTimeoutId = null
    this.autoClose = false
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.receiver) this.receiver.disconnect()
  }

  firstUpdated() {
    this.$content = this.shadowRoot.querySelector('.content')
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      this.handleBodyScroll()
    }
  }

  handleBodyScroll() {
    clearTimeout(this.openTimeout)

    this.openTimeout = setTimeout(() => {
      if (this.open && this.open != 'false') {
        document.documentElement.classList.add('overflow-hidden')
        this.$content?.focus()
      } else if (!this.open || this.open === 'false') {
        document.documentElement.classList.remove('overflow-hidden')
      }
    }, 10)
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }

    if (this.autoClose) {
      this.closeTimeoutId = setTimeout(() => (this.open = false), 3000)
    }
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      return
    }
    event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      class="container"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <div
        class="modal-overlay"
        aria-hidden="true"
        @click="${this.close}"
      ></div>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="-1"
      >
        <hh-focus-trap ?active="${this.open}">
          <button
            @click="${this.close}"
            class="button"
            type="button"
            aria-label="${t('accessibility.close')}"
          >
            ${unsafeHTML(paths.close)}
          </button>
          <slot></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      box-sizing: border-box;
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      width: 100vw;
      height: 100vh;
      visibility: hidden;
      opacity: 0;
      z-index: -1;
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    @media screen and (max-width: 459px) {
      :host([size='flyout']) {
        left: var(--hh-flyout-offset, 30px);
        width: calc(100vw - var(--hh-flyout-offset, 30px) * 2);
      }
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .container {
      position: relative;
      display: flex;
      height: 100%;
      align-items: center;
      // padding: 20px;
    }

    .modal-overlay {
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      background: var(--hh-modal-overlay, rgba(222, 219, 213, 0.2));
      backdrop-filter: blur(5px);
      z-index: 0;
    }

    .button {
      position: absolute;
      padding: 12px;
      width: 42px;
      height: 42px;
      top: 8px;
      right: 8px;
      border: none;
      color: var(--hh-color);
      background-color: transparent;
      cursor: pointer;
      z-index: 2;
    }

    .content {
      position: relative;
      display: flex;
      flex-direction: column;
      // justify-content: center;
      max-height: calc(100vh - (20px * 2));
      overflow: var(--hh-content-overflow, auto);
      width: 100%;
      padding: 20px;
      background-color: var(--hh-background, #FDFCF8);
      box-shadow: 0px 0px 0px 0px rgba(0, 0, 0, 0.1),
        0px 26px 58px 0px rgba(0, 0, 0, 0.1),
        0px 105px 105px 0px rgba(0, 0, 0, 0.09),
        0px 236px 141px 0px rgba(0, 0, 0, 0.05),
        0px 419px 168px 0px rgba(0, 0, 0, 0.01),
        0px 655px 183px 0px rgba(0, 0, 0, 0);
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    :host([size='medium']) .content {
      max-width: var(--hh-max-width, 450px);
      margin: auto;
      height: auto;
      max-height: 75vh;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
